import { expoInOut } from 'svelte/easing';
import { get } from 'svelte/store';
import { slide } from 'svelte/transition';
import { getProductsFromAlgoliaAsync, OPTION_RESPONSE_FIELDS } from '../modules/common/services/algolia';
import { getCMSComponentsFromHybrisAsync } from '../modules/common/services/hybris';
import { unxEscapeSpecialCharacters } from '../modules/common/services/unbxd';
import { getCartLoginDetails } from '../modules/header/services/header-service';
import { addToWishlist } from '../modules/home/services/home-data-services';
import { handleCartResponse } from '../modules/product-details/services/pdp-data-services';
import { getAppConfig } from './config/app-config';
import { APP_CONSTANTS } from './config/app-constants';
import {
  categoryStaticBottomContent,
  categoryStaticTopContent,
  compareProducts,
  isCategoryStaticBottomContent,
  isCategoryStaticTopContent,
  islocationDisabled,
  openRestockablePopup,
  restockableProductContent,
  unxSelectedRatings,
  unxSortOptions,
} from './store/ui-state-store';
const lsKey = 'compare-products';

const {
  unbxdSearchConfig: { filterInfo, filtersTobeExcludedInURL },
  encodedContextPath,
  themeResourcePath,
  currency,
  currencyDecimal,
  lang,
  sponsoredKey,
  extraSponsoredKey,
  userDefaultCityCode,
  userSelectedCityCode,
  joodMegaSaleTag,
  primeLevel,
  svelteATCEnabled,
  unbxdEnabled,
  joodFullCappingLimitReached,
} = getAppConfig();

const filterInfoMap = (<any>Object).groupBy(filterInfo, x => x.code);

export const CONDITION_PRICE_FACETS = ['priceNew', 'priceOpenBox', 'priceRenewed'];

export const OFFER_SORT = {
  priceNew: 'priceValueDiscountPercentageNew',
  priceOpenBox: 'priceValueDiscountPercentageOpenBox',
  priceRenewed: 'priceValueDiscountPercentageRenewed',
};

export const globalCurrencyTag = `<span class="c_global-currency-sign"><span class="global-currency-text">${currency}</span></span>`;

export function checkForMegaSaleProduct(productTags) {
  let isMegaSaleProduct = [];
  if (productTags?.length) {
    isMegaSaleProduct = productTags.filter(tag => {
      return tag.productStatus === joodMegaSaleTag ? tag.productStatus : '';
    });
  }
  return isMegaSaleProduct;
}

export function isMobileWidth() {
  return window.matchMedia('only screen and (max-width: 899px)').matches;
}

export function isMobile() {
  const toMatch = [/Android/i, /webOS/i, /iPhone/i, /iPad/i, /iPod/i, /BlackBerry/i, /Windows Phone/i];
  return (
    toMatch.some(toMatchItem => {
      return navigator.userAgent.match(toMatchItem);
    }) || isMobileWidth()
  );
}

export function getLangSpecificFieldname(fieldname, _lang = lang, suffix = '') {
  if (_lang.length > 0) {
    _lang = _lang[0].toUpperCase() + _lang.substr(1);
  } else {
    _lang = 'Ar';
  }
  return `${fieldname}${_lang}${suffix}`;
}

export function getCountryList(lang) {
  const countryList = [
    { id: 'sa', nameKey: 'header.saudi-arabia', image: `${themeResourcePath}/images/ksa-flag.png` },
    { id: 'bh', nameKey: 'header.bahrain', image: `${themeResourcePath}/images/bah-flag.png` },
    { id: 'om', nameKey: 'header.oman', image: `${themeResourcePath}/images/oma-flag.png` },
    { id: 'eg', nameKey: 'header.egypt', image: `${themeResourcePath}/images/egy-flag.png` },
  ];

  return countryList.map(item => Object.assign(item, { link: `/${lang.toLowerCase()}-${item.id}` }));
}
export function getCountryFullnameEn() {
  const { countryCode } = getAppConfig();
  switch (countryCode?.toLowerCase()) {
    case 'sa':
      return 'Saudi Arabia';
    case 'om':
      return 'Oman';
    case 'bh':
      return 'Bahrain';
  }
  return 'Unknown';
}

export function changeCountry(href) {
  const newLink = window.location.href.replace(encodedContextPath, href);
  window.location.assign(newLink);
}
export function isDirectionRTL() {
  return document.dir === 'rtl';
}

export function parseQueryString(queryString) {
  if (queryString.length === 0) return {};

  const keyValueString = queryString.startsWith('?') ? queryString.substr(1) : queryString;
  const objectArray = keyValueString
    .split('&')
    .filter(x => x !== '')
    .map(x => {
      return { [x.substr(0, x.indexOf('='))]: x.substr(x.indexOf('=') + 1) };
    });

  return Object.assign({}, ...objectArray);
}

export function getRandomString(prefix, length) {
  length = Math.max(length, prefix.length);
  const remLength = length - prefix.length;
  const randomString = Array(remLength)
    .fill(0)
    .map(() => '' + Math.floor(Math.random() * 10))
    .join('');
  return `${prefix}${randomString}`;
}

export function clamp(num, min, max) {
  return num < min ? min : num > max ? max : num;
}

export function linear(domain, range) {
  const d0 = domain[0];
  const r0 = range[0];
  const m = (range[1] - r0) / (domain[1] - d0);

  /** @param {number} num */
  function scale(num) {
    return r0 + (num - d0) * m;
  }

  scale.inverse = () => linear(range, domain);

  return scale;
}

export function isValidColor(strColor) {
  const s = new Option().style;
  s.color = strColor;
  return s.color == strColor.toLowerCase();
}

export function parseLatestOrder(customerLastOrderDetails) {
  if (customerLastOrderDetails === null || customerLastOrderDetails.trim() === '') {
    return false;
  }

  // "{WES900295001=16 June}"
  const lastOrderRE = /\{(.*)=(.*)\}/;
  const reResult = lastOrderRE.exec(customerLastOrderDetails);

  if (reResult !== null) {
    const [, orderId, orderDate] = reResult;
    return { orderId, orderDate };
  }
  return false;
}

export function getLangChangeUrl() {
  const currentUrl = window.location.href;
  const { lang, countryCode } = getAppConfig();
  const currentLocale = `${lang}-${countryCode}`.toLowerCase();
  const newLanguage = lang === 'en' ? 'ar' : 'en';
  const newLocale = `${newLanguage}-${countryCode}`.toLowerCase();

  const langChangeUrl = currentUrl.replace(currentLocale, newLocale);
  return langChangeUrl;
}

export function getCookie(name: string): string | null {
  const cookieObj = Object.fromEntries(document.cookie.split(';').map(x => x.trim().split('=')));

  return name in cookieObj ? cookieObj[name] : null;
}

export function setCookie(name, value, days) {
  let expires = '';
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = '; expires=' + date.toUTCString();
  }
  document.cookie = name + '=' + (value || '') + expires + '; path=/';
}

function zeroPadNumber(num) {
  if (num === undefined) {
    return '';
  }
  let nStr = num.toString();
  if (nStr.length === 1) {
    nStr = '0' + nStr;
  }
  return nStr;
}
export function formatDealTimer(dealTimer) {
  return Object.assign({}, dealTimer, {
    hDiff: zeroPadNumber(dealTimer.hDiff),
    minDiff: zeroPadNumber(dealTimer.minDiff),
    secDiff: zeroPadNumber(dealTimer.secDiff),
  });
}

/**
 * Function ported from acc.productlist-algolia.js
 * @param endDateDeal received from algolia response => hit objects
 */
export function createDealTimerObject(endDateDeal, includeDaysInHours = true) {
  // time correction
  if (typeof endDateDeal === 'string') {
    if (endDateDeal.endsWith('Z') || endDateDeal.endsWith('z')) {
      endDateDeal = endDateDeal.replace(/z/i, '+0300');
    } else if (!endDateDeal.includes('+')) {
      // endDateDeal does not have 'z', 'Z' or '+'
      // append +0300 to treat it in KSA timezone
      endDateDeal += '+0300';
    }
  }

  const dealDate = new Date(new Date(endDateDeal).toUTCString());
  const nowDate = new Date(new Date().toUTCString());
  let msDifference = dealDate.getTime() - nowDate.getTime();

  let days = Math.floor(msDifference / (1000 * 60 * 60 * 24));
  msDifference %= 1000 * 60 * 60 * 24;
  let hours = Math.floor(msDifference / (1000 * 60 * 60));
  msDifference %= 1000 * 60 * 60;
  const minutes = Math.floor(msDifference / (1000 * 60));
  msDifference %= 1000 * 60;
  const seconds = Math.floor(msDifference / 1000);

  if (includeDaysInHours) {
    hours += days * 24;
    days = 0;
  }

  const h = hours > 9 ? hours.toString() : '0' + hours.toString();
  const m = minutes > 9 ? minutes.toString() : '0' + minutes.toString();
  const s = seconds > 9 ? seconds.toString() : '0' + seconds.toString();

  return {
    deals: msDifference > 0 ? true : false,
    dDiff: days,
    hDiff: h,
    minDiff: m,
    secDiff: s,
    distance: null,
  };
}

export function loadProdFromLS() {
  const compareProdLS = localStorage.getItem(lsKey);
  if (compareProdLS) {
    compareProducts.set(JSON.parse(compareProdLS));
  } else {
    compareProducts.set([]);
  }
}

export function removeAllProdFromLS() {
  compareProducts.set([]);
  const pageUrl = `${location.origin}${encodedContextPath}`;
  const url = `${pageUrl}/compare-product/remove-all-product`;
  addRemoveCompareProd(url);
  saveProdToLocalStorage();
}

export function saveProdToLocalStorage() {
  const pageUrl = `${location.origin}${encodedContextPath}`;

  if (get(compareProducts).length > 0) {
    const productSku = get(compareProducts)[get(compareProducts).length - 1].productCode;
    const url = `${pageUrl}/compare-product/add-product?productCode=${productSku}`;
    localStorage.setItem(lsKey, JSON.stringify(get(compareProducts)));
    addRemoveCompareProd(url);
  } else {
    localStorage.removeItem(lsKey);
  }
}

export function addRemoveCompareProd(url) {
  fetch(url)
    .then(response => response.json())
    .catch(err => console.log(err));
}

export function parseCashback(cashbackString, _countryCode) {
  if (cashbackString && cashbackString.length > 0) {
    const cashbackArray = cashbackString.split('-');
    const cashBackPrice = Number(cashbackArray[0])?.toFixed(currencyDecimal);
    return cashBackPrice;
  }
  return 0;
}

export function parsePriceString(priceString, _countryCode) {
  return Number(priceString)?.toFixed(currencyDecimal);
}

export function sortByObjectProperty(array, propertyName, ascending = true) {
  const copy = [...array];

  return (copy as any[]).sort((a, b) => {
    const reverseMux = ascending ? 1 : -1;
    if (a[propertyName] < b[propertyName]) return -1 * reverseMux;
    if (a[propertyName] > b[propertyName]) return +1 * reverseMux;
    return 0;
  });
}
export function refSKUSorter(refSKUArray) {
  return refPropertySorter(refSKUArray, 'productCode');
}
export function refPropertySorter(refSKUArray, propertyName) {
  return function (a, b) {
    return refSKUArray.indexOf(a[propertyName]) - refSKUArray.indexOf(b[propertyName]);
  };
}

export function joinUrls(...urls) {
  return urls.join('/').replace(/\/\//g, '/');
}
export function smartJoinUrls(url1, url2) {
  if (!url2) {
    url2 = '';
  }
  if (url2.startsWith(url1)) return url2;
  return joinUrls(url1, url2);
}

export function joinReadableStrings(strings: string[]) {
  if (strings.length === 0) return '';
  if (strings.length === 1) return strings[0];

  return [strings.slice(0, strings.length - 1).join(', '), strings[strings.length - 1]].join(
    ` ${getStr('extra.pdp.andLable')} `
  );
}
export function roundDownHalfToFixedPlaces(number, decimalPlaces) {
  const decimals = +decimalPlaces;
  const numberRounded = (number - Math.pow(10, -1 * (decimals + 1))).toFixed(decimalPlaces);

  return numberRounded;
}

export function roundToAtMax2Digits(value) {
  const nExp10 = 100;
  return roundToAtMaxNDigits(value, nExp10);
}

export function roundToNDigits(value, n) {
  return roundToAtMaxNDigits(value, Math.pow(10, +n));
}
export function roundToAtMaxNDigits(value, nExp10) {
  if (value === null || value === undefined) {
    return null;
  }

  return Math.round(value * nExp10) / nExp10;
}

export function updateMetaTag(name, content) {
  const tagDOM = document.head.querySelector('meta[name="' + name + '"]');
  if (tagDOM) {
    tagDOM.setAttribute('content', content);
  } else {
    const newMetaTag = document.createElement('meta');
    newMetaTag.name = name;
    newMetaTag.content = content;
    document.head.appendChild(newMetaTag);
  }
}

export function getAttributeString(spllitCharator, stringValue, index) {
  let returnValue = stringValue;
  if (stringValue.indexOf(':') !== -1) {
    returnValue = stringValue.split(spllitCharator)[index];
  }

  return returnValue;
}

// export function createPLPProductTileObject(inputHit: any) {
//   return Object.assign(inputHit, {
//     ...(!!inputHit?.endDateDiscount && { dealTimer: createDealTimerObject(inputHit?.endDateDiscount) }),

//     freegiftInfo: createFreeGiftTileObject(inputHit),
//   });
// }

export function createPLPProductTileObject(inputHit: any, selectedConditions: string[] = []) {
  const product = resolveDisplayOffer(inputHit, selectedConditions);

  return Object.assign(product, {
    ...(!!product?.endDateDiscount && {
      dealTimer: createDealTimerObject(product.endDateDiscount),
    }),

    freegiftInfo: createFreeGiftTileObject(product),
  });
}
export function createFreeGiftTileObject(product: any) {
  if (product?.freegiftworth?.length > 0) {
    const freegiftArray = product.freegiftworth.split('-');
    let freeGiftSet = false;
    let freeGitftWorth = 0;
    let targetQuantity = 0;
    let sourceQuantiy = 0;
    let freeGiftMap = [];
    if (freegiftArray.length === 5) {
      freeGitftWorth = freegiftArray[0];
      targetQuantity = freegiftArray[1];
      sourceQuantiy = freegiftArray[2];
      freeGiftMap = freegiftArray[3];
      freeGiftSet = freegiftArray[4];
    }
    return {
      hasFreegift: true,
      freeGiftSet: freeGiftSet,
      freeGitftWorth: freeGitftWorth,
      targetQuantity: targetQuantity,
      sourceQuantiy: sourceQuantiy,
      freeGiftMap: freeGiftMap,
    };
  }

  return { hasFreegift: false };
}
export function getComingSoonDateFormat(dateString, currentLang) {
  const monthsAR = [
    'يناير',
    'فبراير',
    'مارس',
    'إبريل',
    'مايو',
    'يونيو',
    'يوليو',
    'أغسطس',
    'سبتمبر',
    'أكتوبر',
    'نوفمبر',
    'ديسمبر',
  ];
  const monthsEn = ['Jan', 'Feb', 'March', 'Apr', 'May', 'June', 'July', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  let returnDateValue = dateString;
  if (dateString.indexOf('-') !== -1) {
    const dateArray = dateString.split('-');
    if (dateArray.length === 3) {
      const dayValue = dateArray[0];
      let monthValue = dateArray[1];
      const monthIndex = parseInt(dateArray[1]) - 1;
      const yearValue = dateArray[2];
      if (currentLang.toLowerCase() === 'en') {
        if (typeof monthsEn[monthIndex] !== 'undefined') {
          monthValue = monthsEn[monthIndex];
        }
      } else {
        if (typeof monthsAR[monthIndex] !== 'undefined') {
          monthValue = monthsAR[monthIndex];
        }
      }
      returnDateValue = dayValue + ' ' + monthValue + ',' + ' ' + yearValue;
    }
  }

  return returnDateValue;
}

export function formatNumber(n) {
  return Intl.NumberFormat('en-US', {
    minimumFractionDigits: currencyDecimal ? Number(currencyDecimal) : null,
    maximumFractionDigits: currencyDecimal ? Number(currencyDecimal) : null,
    useGrouping: false,
  }).format(n);
}
export function numberTo2Digits(n) {
  if (n !== undefined && n !== null) {
    const nn = +n; // convert to number, if not already
    return nn >= 0 && n <= 9 ? `0${nn}` : `${nn}`;
  }
  return '';
}
export function getStr(keyOfACCPageLabels, args = [], defaultValue = '') {
  if (window['ACC'].config.pageLabels) {
    const labelString =
      keyOfACCPageLabels in window['ACC'].config.pageLabels
        ? window['ACC'].config.pageLabels[keyOfACCPageLabels]['label']
        : defaultValue || keyOfACCPageLabels;

    return args.reduce((str, val, index) => str.replaceAll(`{${index}}`, val), labelString);
  }

  return keyOfACCPageLabels;
}

export function getLabelColor(keyOfACCPageLabels) {
  if (window['ACC'].config.pageLabels) {
    return keyOfACCPageLabels in window['ACC'].config.pageLabels
      ? window['ACC'].config.pageLabels[keyOfACCPageLabels]['labelColor']
      : null;
  }
  return null;
}

export function getBackgroundColor(keyOfACCPageLabels) {
  if (window['ACC'].config.pageLabels) {
    return keyOfACCPageLabels in window['ACC'].config.pageLabels
      ? window['ACC'].config.pageLabels[keyOfACCPageLabels]['backgroundColor']
      : null;
  }
  return null;
}

export function enterKeyEvent(node) {
  const handleInput = event => {
    if (event.key === 'Enter') {
      node.dispatchEvent(new CustomEvent('enterKey'));
    }
  };
  node.addEventListener('keypress', handleInput);

  return {
    destroy() {
      node.removeEventListener('keypress', handleInput);
    },
  };
}
export function validityEvent(node) {
  const handleInput = () => {
    if (node.validity.valid) {
      node.dispatchEvent(new CustomEvent('validityChange', { detail: true }));
    } else {
      node.dispatchEvent(new CustomEvent('validityChange', { detail: false }));
    }
  };
  setTimeout(handleInput);
  node.addEventListener('input', handleInput);
  return {
    destroy() {
      node.removeEventListener('input', handleInput);
    },
  };
}

export function checkLocationDisabled() {
  navigator.permissions
    .query({ name: 'geolocation' })
    .then(async function (result) {
      try {
        if (result.state === 'granted') {
          islocationDisabled.set(false);
        }
      } catch (error) {
        console.error(error);
      }
    })
    .catch(function (error) {
      console.error('Error checking geolocation permission:', error);
    });
}

export function getDateStringInCurrentLang(dateString, lang) {
  if (lang === 'en') {
    return dateString; // no transformation for english
  }
  dateString = (dateString && dateString.trim()) || '';
  const d = new Date(dateString);
  if (d instanceof Date && isFinite(d.getDate())) {
    // Thu 13 Jul 2023
    const days = window['ACC'].main.infos.days;
    const months = window['ACC'].main.infos.months;
    const day = days[d.getDay()];
    const date = d.getDate();
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    return `${day} ${date} ${month} ${year}`;
  }
  return ''; // return blank if not a valid date
}

export function isPointWithinElementBounds(mouseClickEvent, targetElement) {
  // returns whether mouse click was done inside targetElement
  const { x, y, width, height } = targetElement.getBoundingClientRect();
  const { x: mouseX, y: mouseY } = mouseClickEvent;

  const xBound = mouseX >= x && mouseX <= x + width;
  const yBound = mouseY >= y && mouseY <= y + height;

  return xBound && yBound;
}

export function getContextedUrl(url: string) {
  if (!url) {
    return '';
  }
  if (url.startsWith('http') || url.startsWith(encodedContextPath)) {
    return url;
  }
  if (url.length > 0 && url[0] !== '/') {
    url = '/' + url;
  }
  return encodedContextPath + url;
}

// usage: <p use:lineEnforcer={{ lines: 2, appendClassOnOverflow: 'class-name-to-add-when-overflow-occurs' }}>Content on which the enforcer will work</p>
export function lineEnforcer(node: HTMLElement, params) {
  // params example: { lines: number }
  const nLines = params?.lines || 5;
  const computedStyles = getComputedStyle(node);
  const strLineHeight = computedStyles.lineHeight;
  const nLineHeight = parseFloat(strLineHeight);
  const nTotalHeight = nLineHeight * nLines;
  const strTotalHeight = strLineHeight.replace(nLineHeight.toString(), nTotalHeight.toString());
  node.style.maxHeight = strTotalHeight;
  node.style.overflowY = 'hidden';

  // append classname if scrollHeight > clientHeight
  if (params.appendClassOnOverflow) {
    const { scrollHeight } = node;
    const isOverflowY = Math.round(scrollHeight / nLineHeight) > nLines;
    if (isOverflowY) {
      node.classList.add(params.appendClassOnOverflow);
    } else {
      node.classList.remove(params.appendClassOnOverflow);
    }
  }
}

export function getTitleCasedString(str, onlyFirstCharacter) {
  if (str?.length > 0) {
    if (onlyFirstCharacter) {
      return str[0].toUpperCase() + str.slice(1).toLowerCase();
    } else {
      return str
        .split(' ')
        .map(s => s[0].toUpperCase() + s.slice(1).toLowerCase())
        .join(' ');
    }
  }
  return '';
}

export function reverseChildNodes(node, params) {
  // if reverse is not defined in params, it is assumed true
  if (params.reverse ?? true) {
    const reversedArray = Array.from(node.childNodes.entries())
      .map(x => x[1])
      .reverse();
    node.innerHTML = '';
    reversedArray.forEach(r => node.appendChild(r));
  }
}
export function reverseChildNodesForArabicLang(node) {
  const reverse = lang === 'ar';
  return reverseChildNodes(node, { reverse });
}

export type JoodMembershipType = 'NOPRIME' | 'BASICPRIME' | 'VIPPRIME' | 'SMARTPRIME';
export type JoodType = 'NOPRIME' | 'BASIC' | 'VIP' | 'SMART';
export function getJoodExpiredMembershipType() {
  const { primeEnabled, joodExpiredFlag } = getAppConfig();
  if (primeEnabled && joodExpiredFlag) {
    return `${joodExpiredFlag}PRIME`;
  }
  return null;
}

export function getJoodMembershipType(): JoodMembershipType {
  const { primeEnabled, primeLevel, joodFullCappingLimitReached } = getAppConfig();
  const isPrimeEnabled = primeEnabled == 'true';

  // return value can be one of these strings:
  // NOPRIME (for non-prime users)
  // BASICPRIME (for blue prime users)
  // VIPPRIME (for gold prime users)
  // SMARTPRIME (for smart prime users)
  const membershipType =
    isPrimeEnabled && String(joodFullCappingLimitReached) !== 'true' && primeLevel ? primeLevel : 'NOPRIME';
  return membershipType;
}

export function getJoodMembershipBasedPropertyValue(obj, noPrimeProp, basicPrimeProp, vipPrimeProp) {
  const memberType = getJoodMembershipType();
  let targetPropertyName = noPrimeProp;
  if (memberType === 'BASICPRIME' && basicPrimeProp in obj) targetPropertyName = basicPrimeProp;
  if (memberType === 'VIPPRIME' && vipPrimeProp in obj) targetPropertyName = vipPrimeProp;

  return obj[targetPropertyName];
}

export function applyRTLArrowCss(parent, prevLeftCss, prevRightCss, nxtLeftCss, nxtRightCss) {
  if (document.dir === 'rtl' && parent && parent.length > 0) {
    parent.forEach(function (section) {
      const nextArrow = section.querySelector('.splide__arrow--next');
      const prvArrow = section.querySelector('.splide__arrow--prev');
      if (prvArrow) {
        prvArrow.style.setProperty('left', prevLeftCss, 'important');
        prvArrow.style.setProperty('right', prevRightCss, 'important');
      }
      if (nextArrow) {
        nextArrow.style.setProperty('left', nxtLeftCss, 'important');
        nextArrow.style.setProperty('right', nxtRightCss, 'important');
      }
    });
  }
}

export function isValueSponsoredOrExtraSponsored(value) {
  return (
    sponsoredKey.length > 0 &&
    extraSponsoredKey.length > 0 &&
    typeof value === 'string' &&
    [sponsoredKey, extraSponsoredKey].indexOf(value) > -1
  );
}
export function isValueExtraSponsored(value) {
  return isValueSponsoredOrExtraSponsored(value) && value === extraSponsoredKey;
}
export function isValueSponsored(value) {
  return isValueSponsoredOrExtraSponsored(value) && value === sponsoredKey;
}

export function slideOpenAnimation(node) {
  return slide(node, { axis: 'y', easing: expoInOut, duration: 250 });
}

export function getPriceHTMLOrFree(value) {
  if (+value > 0) {
    if (lang === 'en') {
      return `${globalCurrencyTag} <strong>${Number(value)?.toFixed(currencyDecimal)}</strong>`;
    } else {
      return `<strong>${Number(value)?.toFixed(currencyDecimal)}</strong> ${globalCurrencyTag}`;
    }
  }
  return `<strong>${getStr('text.free')}</strong>`;
}

export function convertJsonObjectToFormData(formJson, keyPrefix = '') {
  let output = [];
  for (const [key, value] of Object.entries(formJson)) {
    if (Array.isArray(value)) {
      if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
        output = [...output, ...value.map((v, i) => convertJsonObjectToFormData(v, `${keyPrefix}${key}[${i}].`))];
      } else {
        output.push(`${key}=${value.join(',')}`);
      }
    } else if (typeof value === 'object' && value !== null) {
      output = [...output, convertJsonObjectToFormData(value, `${keyPrefix}${key}.`)];
    } else if (value === undefined || value === null) {
      // do nothing
      output.push(`${keyPrefix}${key}=`);
    } else {
      output.push(`${keyPrefix}${key}=${value}`);
    }
  }
  return output.join('&');
}

export function mergeObjectsBySimilarProperties(objectArray) {
  return objectArray.reduce((a, b) => {
    if (!a) return Object.assign({}, b);
    const c = {};
    for (const key in a) {
      if (key in b) {
        c[key] = [a[key], b[key]].join(', ');
      }
    }
    return c;
  }, null);
}
export function getProductDataForGTM(prdData, index) {
  return {
    nameEn: prdData?.nameEn,
    productCode: prdData?.code || prdData?.productCode,
    brandEn: prdData?.brandEn,
    allCategoriesEn: prdData?.allCategoriesEn,
    priceValueDiscount: prdData?.priceValueDiscount || (prdData?.discountPrice && prdData?.discountPrice.value),
    aftreCashBackPrice:
      prdData?.aftreCashBackPrice || (prdData?.aftreCashBackPrice && prdData?.aftreCashBackPrice.value),
    allPromotions: prdData?.allPromotions,
    price: prdData?.price,
    wasPrice: prdData?.wasPrice || (prdData?.price && prdData?.price.value),
    variantsEn: prdData?.variantsEn,
    productIndex: index ? index : 0,
    pageCategoryNameEn: prdData.pageCategoryNameEn,
    pageSubCategoryNameEn: prdData.pageSubCategoryNameEn,
    rmsCategoryNameEn: prdData.rmsCategoryNameEn,
    productUrl: prdData?.productUrl,
    urlEn: prdData?.urlEn ? prdData?.urlEn : prdData?.url,
    urlAr: prdData?.urlAr ? prdData?.urlAr : prdData?.url,
  };
}

export function sortByPosition(a, b) {
  return a.position - b.position;
}
export function unxGetCountsFromFacetValues(values: any[]): string[] {
  return values?.filter((_v, i) => i % 2 !== 0) || [];
}
export function unxGetKeysFromFacetValues(values: any[]): string[] {
  return values?.filter((_v, i) => i % 2 === 0) || [];
}
export function unxValuesToObjectArray(values: any[], facetName = null) {
  // input [ 'val1', count1, 'val2', count2 ]
  // output [ { value: 'val1', count: count1 }, { value: 'val2', count: count2 }]

  if (values?.length > 1) {
    let output = [];
    for (let i = 0; i < values.length - 1; i += 2) {
      output.push({
        value: values[i],
        count: values[i + 1],
      });
    }
    const currentCityCode = userSelectedCityCode || userDefaultCityCode;
    if (['deliveryFacet_uFilter', 'inStockCities_uFilter'].indexOf(facetName) > -1) {
      output = output.filter(x => x.value.startsWith(currentCityCode) || x.value === 'codEnabled');
    } else if (facetName === 'inStock_uFilter') {
      const cityStockString = `${currentCityCode}_inStock`;
      output = output.filter(x => x.value === cityStockString);
    } else if (facetName === 'offersFacet_uFilter') {
      const goingOutOfStockSuffix = '_goingOutOfStock';
      const acceptedGoingOOSValue = `${currentCityCode}${goingOutOfStockSuffix}`;
      output = output.filter(x => !x.value.endsWith(goingOutOfStockSuffix) || x.value === acceptedGoingOOSValue);
    } else if (facetName === 'isOnlineOnly_uFilter') {
      output = output.filter(x => x.value === false);
    }

    return output;
  }
  return [];
}
export function algoliaToUnbxdSortString(algoliaSortString: string) {
  if (algoliaSortString === 'relevance') return '';
  return algoliaSortString?.replace(/_/g, ' ').trim() || '';
}

export function composeURLFromUnbxdResponseQueryParams(searchQueryParamsFromUnbxdResponse: any): URL {
  const { filter, page = 0, q = '', rows = 24, sort = 'relevance' } = searchQueryParamsFromUnbxdResponse;
  const url = new URL(document.location.pathname, document.location.origin);

  if (filter) {
    const filterArr = Array.isArray(filter) ? [...filter] : [filter];
    const _q = composeQFromUnbxdFilterArray(filterArr, q);
    url.searchParams.set('q', _q);
  }
  if (q !== undefined) {
    url.searchParams.set('text', q);

    if (url.pathname.includes('/c/')) {
      const seoUrl = generateSEOUrlForCategoryUrl(url.pathname, url.searchParams.get('q'));
      if (seoUrl?.length > 0) {
        url.pathname = url.pathname.split('/facet/')[0] + seoUrl;
      }
    }
  }
  if (page !== undefined) {
    url.searchParams.set('pg', page);
  }
  if (rows !== undefined) {
    url.searchParams.set('pageSize', rows);
  }
  if (sort !== undefined) {
    if (['', 'relevance'].includes(sort)) {
      url.searchParams.set('sort', 'relevance');
    } else {
      let sortValue = sort;
      if (sortValue.includes(',')) {
        const sortArray = sortValue.split(',');
        sortValue = sortArray[sortArray?.length - 1].trim();
      }
      const selectedSortObject = (<any[]>get(unxSortOptions)).find(x => x.sortSuffix === sortValue);
      if (selectedSortObject?.code) {
        url.searchParams.set('sort', selectedSortObject.code);
      }
    }
  }
  // const updatedUrl = removeTrailingSlash(url);
  return url;
}

// function removeTrailingSlash(url: URL): URL {
//   if (url?.pathname?.endsWith('/') && url?.search) {
//     url.pathname = url.pathname.slice(0, -1);
//   }
//   return url;
// }
export function replaceSpecialCharactersInURL(targetURL: string, restoreToOriginal: boolean = false) {
  let output = decodeURIComponent(targetURL);
  if (output.includes(':"')) {
    output = output.replaceAll('"', '');
  }
  const specialCharacterMap = {
    '"': '$inch',
    '°': '$degree',
    '&': '$and',
    '-': '$dash',
    ',': '$comma',
    '+': '$plus',
    '²': '$square',
    ';': '$semicolon',
    "'": '$apostrophe',
    '/': '__',
  };

  const replaceSources = restoreToOriginal ? Object.values(specialCharacterMap) : Object.keys(specialCharacterMap);
  const replaceTargets = restoreToOriginal ? Object.keys(specialCharacterMap) : Object.values(specialCharacterMap);

  for (let i = 0; i < replaceSources.length; i++) {
    if (restoreToOriginal) {
      replaceTargets[i] = encodeURIComponent(replaceTargets[i]);
    }
    output = (<any>output).replaceAll(replaceSources[i], replaceTargets[i]);
  }

  return output;
}
export function generateSEOUrlForCategoryUrl(_pathname, qParamString) {
  let wip: string = qParamString;
  if (wip?.startsWith(':relevance')) wip = wip?.substring(10);
  if (wip?.startsWith(':')) wip = wip?.substring(1);

  const b = wip?.split(':');
  const c = [];
  for (let i = 0; i < b?.length - 1; i += 2) {
    if (filtersTobeExcludedInURL.includes(b[i])) {
      continue;
    } else {
      c.push(b[i]);
      c.push(b[i + 1]);
    }
  }
  wip = c.join(':');
  wip = replaceSpecialCharactersInURL(wip); // wip.replace(/["°&-,+²;/']/g, m => charsToReplace[m]);
  wip = `/facet/${wip}`;

  return wip;
}
export function parseUnbxdResponseQueryParamsToSearchParams(searchQueryParamsFromUnbxdResponse: any): URLSearchParams {
  const url = composeURLFromUnbxdResponseQueryParams(searchQueryParamsFromUnbxdResponse);
  return parseUrlToUnbxdSP(url.href);
}
export function composeUrlFromUnbxdResponseQueryParams(searchQueryParamsFromUnbxdResponse: any) {
  return composeURLFromUnbxdResponseQueryParams(searchQueryParamsFromUnbxdResponse).href;
}
export function parseUrlToUnbxdSP(url: string): URLSearchParams {
  /**
   * commenting this out as it is causing issues with the url parsing in some cases.
   * The special characters are being replaced in the url and then the url is being parsed which is causing issues with the search params.
   * We will handle the special characters in the parseQToUnbxdSearchParams function instead.
   *
   */
  //url = replaceSpecialCharactersInURL(url, true);
  const isRelativeUrl = !url.startsWith('http');
  const urlObject = isRelativeUrl ? new URL(url, 'https://www.extra.com') : new URL(url);

  const text = urlObject.searchParams.get('text');
  let q = urlObject.searchParams.get('q');
  if (!q && urlObject.pathname.includes('/facet/')) {
    q = decodeURIComponent(urlObject.pathname.split('/facet/')[1]);
  }
  const pageSize = urlObject.searchParams.get('pageSize');
  const pg = urlObject.searchParams.get('pg');
  let sort = urlObject.searchParams.get('sort');
  if (!sort) sort = 'relevance';
  const selectedSortObject = (<any[]>get(unxSortOptions)).find(x => x.code === sort);

  const unbxdParams: any = {
    ...(!!text && { q: text }),
    ...(!!pageSize && { rows: +pageSize }),
    ...(!!pg && { page: +pg }),
    ...(!!selectedSortObject.sortSuffix && { sort: selectedSortObject.sortSuffix }),
  };
  const unxParams = new URLSearchParams(unbxdParams);
  const qParsedSP = parseQToUnbxdSearchParams(q, text);

  for (const [key, value] of qParsedSP.entries()) {
    unxParams.append(key, value);
  }

  // for category urls, append filter=categories:"<code>"
  if (url.startsWith('c/')) url = `/{url}`;
  if (url.indexOf('/c/') > -1) {
    const { category } = getAppConfig();
    if (category?.ruleName?.length > 0) {
      const { ruleName } = category;
      unxParams.append('q', ruleName);
      unxParams.append('facet', 'true');
    } else {
      const categoryCode = urlObject.pathname.split('/c/')[1].split('/')[0];
      if (categoryCode) {
        unxParams.append('p', `categories_uFilter:"${categoryCode}"`);
        unxParams.append('pagetype', 'boolean');
        unxParams.append('facet', 'true');
      }
    }
  }

  return unxParams;
}

export function combineSPByOverriding(sp1: URLSearchParams, sp2: URLSearchParams) {
  const targetSP = new URLSearchParams(sp1);
  for (const [k, v] of sp2) {
    if (k === 'filter') {
      // combine filter
      const filterName = v.substring(0, v.indexOf(':'));
      const existing = targetSP.getAll('filter').find(x => x.startsWith(filterName));
      if (existing) {
        targetSP.delete('filter', existing);
      }
      targetSP.append('filter', v);
    } else {
      targetSP.set(k, v);
    }
  }
  return targetSP;
}

export function parseQToUnbxdSearchParams(q: string, text: string): URLSearchParams {
  const parsed = parseQ(q, text);
  const sp = new URLSearchParams();

  for (const [key, value] of Object.entries(parsed)) {
    const filterKey = 'filter';
    const filterValue = singleUnbxdFacetToFilterString(key, <any[]>value);
    sp.append(filterKey, filterValue);
  }
  return sp;
}
export function singleUnbxdFacetToFilterString(facetName: string, values: string[]): string {
  const priceFacets = ['price', ...CONDITION_PRICE_FACETS];
  if (priceFacets.includes(facetName)) {
    const priceRange = replaceSpecialCharactersInURL(values[0], true).replace('-', ' TO ');
    return `${facetName}:[${priceRange.replace(/[[\]]/g, '')}]`;
  } else if (facetName === 'ratingFacet') {
    // coming from parseUrl
    const minRatingSelected = Math.min(...values.map(v => +v));
    if (get(unxSelectedRatings) === null) {
      unxSelectedRatings.setSingleRatingStatus(minRatingSelected, true);
    }
    return `rating:[${minRatingSelected} TO 5]`;
  } else if (facetName === 'rating') {
    // coming from unbxd params
    return values[0].startsWith('[') ? `rating:${values[0]}` : values[0];
  } else if (facetName === 'inStock_uFilter') {
    const currentCity = userSelectedCityCode || userDefaultCityCode;
    // const value = `inStockCities:"${currentCity}" OR sellingOutFastCities:"${currentCity}"`;
    const value = `inStock_uFilter:${currentCity}_inStock`;
    return value;
  } else
    return values
      ?.map(v =>
        typeof v === 'string' && v?.includes(' ')
          ? `${facetName}:"${unxEscapeSpecialCharacters(v)}"`
          : `${facetName}:${unxEscapeSpecialCharacters(v)}`
      )
      .join(' OR ');
}
function parseQ(q: string, text: string) {
  const output = {};
  if (!q) return output;

  const splits = q.split(':').filter(x => x?.length > 0);
  const unwantedStarts = ['', text, 'relevance'];
  for (const word of unwantedStarts) {
    if (splits[0] === word) splits.splice(0, 1);
  }

  for (let i = 0; i < splits.length - 1; i += 2) {
    let key = splits[i];
    const value = splits[i + 1];

    if (['price', 'ratingFacet', 'type', 'restockableCities', ...CONDITION_PRICE_FACETS].indexOf(key) === -1) {
      key += '_uFilter';
    }
    if (key in output) output[key].push(value);
    else output[key] = [value];
  }
  return output;
}
function composeQFromUnbxdFilterArray(filterArray: any[], searchTerm: string): string {
  const filterStringified = filterArray
    .map((f: string) => {
      let temp = f
        .replace(/ OR /g, ':')
        .replace(/(_uFilter|\[|\]|\\)/g, '')
        .replace(/ TO /, '-')
        .replace(/rating:(\d)-\d/, 'ratingFacet:$1');
      if (temp.startsWith('inStock')) {
        temp = 'inStock:true';
      }
      temp = replaceSpecialCharactersInURL(temp);
      return temp;
    })
    .join(':');

  return `${searchTerm}:relevance:${filterStringified}`;
}
export function combineURLSearchParams(searchParamArray: URLSearchParams[]) {
  const targetSP = new URLSearchParams();
  for (const sp of searchParamArray) {
    for (const [spKey, spValue] of sp.entries()) {
      if (spKey === 'filter') targetSP.append(spKey, spValue);
      else targetSP.set(spKey, spValue);
    }
  }
  return targetSP;
}
export function getLabelForFacetValue(value, facetName) {
  if (facetName === 'price') return value.replace(/\[(\d+) TO (\d+)\]/, `$1 - $2 ${globalCurrencyTag}`);
  if (facetName === 'rating') return value.replace(/\[(\d) TO \d\]/, `$1 ${getStr('facet.range.up')}`);
  if (facetName === 'deliveryFacet_uFilter') {
    // filterInfoMap
    if ((<string>value)?.endsWith('_homeDeliveryEnabled')) return filterInfoMap['deliveryFacetHomeDelivery'][0].name;
    else if ((<string>value)?.endsWith('_collectFromStoreEnabled'))
      return filterInfoMap['deliveryFacetCollectFromStore'][0].name;
    else if ((<string>value)?.endsWith('_collectionPointEnabled'))
      return filterInfoMap['deliveryFacetCollectionPoint'][0].name;
    else if (value === 'codEnabled') return filterInfoMap['deliveryFacetCodEnabled'][0].name;
  }
  if (facetName === 'inStockCities_uFilter') {
    return getStr('text.quote.yes.button.label');
  }
  if (facetName === 'inStock_uFilter') {
    return filterInfoMap['availabilityFacetInStock'][0].name;
  }
  if (facetName === 'offersFacet_uFilter') {
    if ((value as string).endsWith('_goingOutOfStock')) return filterInfoMap['offersFacetGoingOutOfStock'][0].name;
    const filterInfoString = `facet.productStatus.${value}`;
    if (filterInfoString in filterInfoMap) {
      return filterInfoMap[filterInfoString][0].name;
    } else {
      return value;
    }
  }
  if (facetName === 'soldByFacet_uFilter') {
    const key = `extra.marketplace.soldByFacet.${value}`;
    return filterInfoMap[key]?.[0]?.name ?? value;
  }
  if (facetName === 'isOnlineOnly_uFilter') {
    return filterInfoMap['extra.marketplace.facet.onlineonly']?.[0]?.name ?? value;
  }
  if (facetName === 'purchaseOptions_uFilter') {
    return filterInfoMap[`extra.marketplace.condition.${value}`]?.[0]?.name ?? value;
  }

  if (value === true) return getStr('text.quote.yes.button.label');
  if (value === false) return getStr('text.quote.no.button.label');

  return value;
}
export function gtmViewListEvent(productdData) {
  if (productdData.length > 0) {
    window['ACC'].extragtm.click.trackViewList('', '', productdData);
  }
}
export function jsonBooleanReviver(_: string, value: any) {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return value;
}

export function boolToYesNo(bool: boolean): string {
  return bool ? 'Yes' : 'No';
}

export function onClickWishlist(data, productCode) {
  data.detail.event.preventDefault();
  data.detail.event.stopPropagation();

  const hideLoader = () => window['ACC'].loader.hideLoader();
  const handleError = err => console.log('error: ' + err);
  const updateWishlistCount = wishlistSize => {
    const wishlistCountElement = document.querySelector('.js-wishlist-count');
    if (wishlistCountElement) {
      wishlistCountElement.innerHTML = wishlistSize;
    }
  };

  const handleResponse = res => {
    if (!res.success) {
      window.location.assign(res.redirectUrl);
    } else {
      getCartLoginDetails();
      updateWishlistCount(res.wishlistSize);
    }
  };

  const addToWishlistAsync = async () => {
    try {
      const res = await addToWishlist(productCode);
      handleResponse(res);
    } catch (err) {
      handleError(err);
    } finally {
      hideLoader();
    }
  };

  addToWishlistAsync();
}

export function removeHTMLScrollbar(remove: boolean = true) {
  document.querySelector('html').style.overflow = remove ? 'hidden' : 'auto';
}
export function isRunningUnderAppWebView() {
  return typeof window.ACC.main.appdetected !== 'undefined';
}

// This method will take care of OOS, Restockable, Jood day and other normal tags
// Other duplicate methods will be removed
export function getProductTags(product, isAddToCartPopupTags = false) {
  let outputTags = [];
  const cityCode = userSelectedCityCode || userDefaultCityCode;

  const isComingSoon = !!product?.comingSoon;
  const isInStock =
    !isComingSoon &&
    ('inStockCities' in product
      ? product?.inStockCities?.includes(cityCode)
      : product?.stock?.stockLevelStatus === 'inStock' || product?.stock?.stockLevelStatus?.code === 'inStock');
  const isSellingOutFast =
    !isInStock &&
    ('sellingOutFastCities' in product
      ? product?.sellingOutFastCities?.includes(cityCode)
      : product?.stock?.stockLevelStatus === 'lowStock' || product?.stock?.stockLevelStatus?.code === 'lowStock');
  const isProductInStock = isInStock || isSellingOutFast;
  const isRestockable = !isProductInStock && product?.restockableCities?.includes(cityCode);
  const restockableTagString: string = product[getLangSpecificFieldname('restockableTag')] || null;

  if (isRestockable && !!restockableTagString) {
    const restockableTag = parseSingleTagFromString(restockableTagString);
    outputTags.push(restockableTag);
  } else if (!isProductInStock && !isComingSoon && !isAddToCartPopupTags) {
    const { outOfStock } = getAppConfig();
    outputTags.push({ label: outOfStock, theme: 'gray', borderRadLBot: 12, borderRadRBot: 12 });
  } else if (product && product[getLangSpecificFieldname('productStatus', lang) + 'List']?.length > 0) {
    outputTags = product[getLangSpecificFieldname('productStatus', lang) + 'List'].map(tagString =>
      parseSingleTagFromString(tagString)
    );
  } else if (product?.productTags?.length > 0) {
    outputTags = product.productTags;
  }
  const isJoodDayOffExists = outputTags.some(tag => tag.label === getStr('jood.day.offer.text'));
  // prime tags
  const { joodExpiredFlag } = getAppConfig();
  if (isProductInStock && !isRestockable && !isJoodDayOffExists) {
    if (product?.tacticalBasicPromo || product?.tacticalVipPromo) {
      if (primeLevel === 'BASICPRIME' || joodExpiredFlag === 'BASIC') {
        outputTags.unshift({
          label: getStr('jood.day.offer.text'),
          bgImage: getStr('prime.jood.day.bg.BASIC'),
          textColorCode: getStr('prime.jood.day.color.BASIC'),
          bgRepeat: 'no-repeat',
        });
      } else if (primeLevel === 'VIPPRIME' || joodExpiredFlag === 'VIP') {
        outputTags.unshift({
          label: getStr('jood.day.offer.text'),
          bgImage: getStr('prime.jood.day.bg.VIP'),
          textColorCode: getStr('prime.jood.day.color.VIP'),
          bgRepeat: 'no-repeat',
        });
      } else if (primeLevel === 'SMARTPRIME' || joodExpiredFlag === 'SMART') {
        outputTags.unshift({
          label: getStr('jood.day.offer.text'),
          bgImage: getStr('prime.jood.day.bg.SMART'),
          textColorCode: getStr('prime.jood.day.color.SMART'),
          bgRepeat: 'no-repeat',
        });
      } else {
        outputTags.unshift({
          label: getStr('jood.day.offer.text'),
          bgImage: getStr('prime.jood.day.bg.NONPRIME'),
          textColorCode: getStr('prime.jood.day.color.NONPRIME'),
          bgRepeat: 'no-repeat',
        });
      }
    }
  }
  if ([true, 'true'].includes(joodFullCappingLimitReached)) {
    outputTags = outputTags.filter(tag => tag.label !== getStr('jood.day.offer.text'));
  }

  return outputTags;
}

// example input: "Restocking Soon|RE_STOCKABLE|false|#000000|10||#FFFFFF"
export function parseSingleTagFromString(tagString: string) {
  if (tagString?.length > 0) {
    const [label, productStatus, copyToClipBoardAllowed, backgroundColorCode, , iconURL, textColorCode] =
      tagString.split('|');

    return {
      label,
      productStatus,
      copyToClipBoardAllowed: copyToClipBoardAllowed == 'true',
      backgroundColorCode,
      iconURL,
      textColorCode,
    };
  }

  return null;
}

export function roundToPreviousN(val, n) {
  if (val % n === 0) return val;
  return val - (val % n);
}
export function roundToNextN(val, n) {
  if (val % n === 0) return val;
  return val + (n - (val % n));
}
export function dividePriceRangeIntoNParts(min, max, n) {
  let range = max - min;
  let size = range / n;
  // [0-25, 25-50, 50-75, 75-100]
  if (range % n !== 0) {
    // need to adjust the numbers
    min = roundToPreviousN(min, 5);
    max = roundToNextN(max, 5);
    range = max - min;
    size = Math.ceil(range / n);
  }
  return Array(n)
    .fill(min)
    .map((x, i) => x + i * size)
    .map(x => [x, x + size]);
}

/**
 * Adds a SKU to the cart using QAC and handles the response.
 *
 * @param {Object} detail - The details required for adding the SKU to the cart.
 * @param {string} detail.sku - The SKU of the product to add to the cart.
 * @param {boolean} detail.silentAdd - Indicates whether the add operation should be silent (no UI feedback).
 *
 * @returns {Promise<void>} A promise that resolves when the add operation is complete.
 *
 * This function initiates the process of adding a product to the cart and checks
 * if the product was successfully added. If successful, it updates the cart
 * and handles any UI feedback. If there is an error, it displays an error message.
 */
export function manageAtcQacInSvelte({ detail: { sku, silentAdd } }) {
  // Initiates the process to add a SKU to the cart via QAC.
  return window['ACC'].qac._innerAddSKUToCart(sku, silentAdd).then(async response => {
    window.ACC.loader.showLoader();
    // If there's no response, exit the function.
    if (!response) return;
    // Destructure relevant properties from the response.
    const { productAdded, errorMsg, errorMessage } = response;
    const error = errorMsg || errorMessage;
    // Determine if the product was successfully added to the cart.
    const isProductAdded =
      (svelteATCEnabled && productAdded) ||
      (!svelteATCEnabled && response.cartData.productAdded && !!response.addToCartLayer);
    // If the product was added, handle the cart response and fetch the updated cart.
    if (isProductAdded) {
      try {
        const resp: any = await getProductsFromAlgoliaAsync([sku], OPTION_RESPONSE_FIELDS(['response']));
        if (resp?.hits?.length === 0) {
          throw new Error('Empty response from Algolia');
        } else {
          response['productDataForGtm'] = resp?.hits[0];
        }
      } catch (error) {
        console.error(error);
      }
      await handleCartResponse(response);
      await window['ACC'].qac.getQacCartResponse(silentAdd);
      window.ACC.loader.hideLoader();
    } else {
      window.ACC.loader.hideLoader();
      // If there was an error, show a snackbar with the error message.
      window.ACC.main.showCouponCodeSnackbar('', false, getStr(error));
    }
  });
}
/**
 * Handles the global QAC event by adding and removing the event listener for the QAC open event.
 *
 * This function ensures that only one instance of the event listener is active at any time.
 * It first removes any existing listener and then adds a new one to handle the QAC open event.
 */
export function handleGlobalQacHandler() {
  // Define the event name for better readability
  const eventName = window.ACC.qac.var.QAC_OPEN_EVENT_NAME;
  // Remove any existing event listeners to prevent duplicate handlers
  window.removeEventListener(eventName, manageAtcQacInSvelte);

  // Add the event listener to handle the QAC open event
  window.addEventListener(eventName, manageAtcQacInSvelte);
}

/**
 * Handles the global event for opening the restockable popup.
 *
 * This function ensures that only one instance of the event listener is active at any time.
 * It first removes any existing listener and then adds a new one to handle the restockable open event.
 */
export function handleGlobalRestockableHandler() {
  // Define the event name for better readability
  const eventName = window.ACC.qac.var.QAC_OPEN_RESTOCKABLE_POPUP_EVENT_NAME;

  // Remove any existing event listeners to prevent duplicate handlers
  window.removeEventListener(eventName, manageRestockableDialogInSvelte);

  // Add the event listener to handle the QAC open event
  window.addEventListener(eventName, manageRestockableDialogInSvelte);
}

/**
 * Handles the global event for opening the restockable popup.
 *
 * This function is the event listener for the global event "extra:openRestockablePopup".
 * It will be called whenever the event is triggered.
 *
 * @param {{ detail: { product: object } }} event - The event object.
 * @param {object} event.detail.product - The product object that needs to be shown in the popup.
 */
export function manageRestockableDialogInSvelte({ detail: { product } }) {
  // Show the popup with the product information
  restockableProductContent.set(product);
  openRestockablePopup.set(true);
}

export function unxUpdateTopAndBottomSeoContent() {
  if (unbxdEnabled) {
    const { category } = getAppConfig();
    if (category) {
      isCategoryStaticTopContent.set(!!category?.top_Seo_Content);
      categoryStaticTopContent.set({
        heading: category?.top_Seo_Content_Title,
        body: category?.top_Seo_Content,
      });

      isCategoryStaticBottomContent.set(!!category?.bottom_Seo_Content);
      categoryStaticBottomContent.set({
        heading: category?.bottom_Seo_Content_Title,
        body: category?.bottom_Seo_Content,
      });
    } else {
      isCategoryStaticTopContent.set(false);
      isCategoryStaticBottomContent.set(false);
    }
  }
}

export function parseExtraCareData(data) {
  console.log('[parseExtraCareData] data:', data);
  // 1. group the vasPrices by vasTypeGroupCode
  const groups = Object.groupBy(data.vasPrices, (d: any) => d.vasType.vasTypeGroupCode);
  const output: any = [];

  // 2. check and assign 3 year codes to its corresponding 1 year
  for (const groupCode of Object.keys(groups)) {
    let currentGroup = groups[groupCode];
    let hasAtleastOneChildItem = false;
    for (const vasPrice of currentGroup) {
      if (vasPrice.baseServiceCode?.length > 0) {
        const baseServiceCode = vasPrice.baseServiceCode;
        currentGroup.find(vp => vp.productCodes === baseServiceCode).child = vasPrice;
        const parentVasPrice = currentGroup.find(vp => vp.productCodes === baseServiceCode);
        if (parentVasPrice) {
          vasPrice.priceDifference = vasPrice.servicePrice.value - parentVasPrice.servicePrice.value;
          parentVasPrice.child = vasPrice;
          hasAtleastOneChildItem = true;
        }
      }
    }
    if (hasAtleastOneChildItem) {
      // 3. remove all vasPrices which have baseServiceCode
      currentGroup = currentGroup.filter(vp => !vp.baseServiceCode);
    }
    // 4. calculate the minimum price of the tab's children
    const groupMinPrice = currentGroup.reduce((a, x) => Math.min(a, x.servicePrice.value), 999999999);

    // 5. create tab data object array
    output.push({
      minPrice: groupMinPrice,
      vasTypeGroupCode: currentGroup[0].vasType.vasTypeGroupCode,
      vasTypeGroupName: currentGroup[0].vasType.vasTypeGroupName,
      children: currentGroup,
    });
  }

  console.log('[parseExtraCareData] output:', output);
  return output;
}

export function sorterBasedOnAnotherArray(refArray: string[]) {
  return (a, b) => refArray.indexOf(a[0]) - refArray.indexOf(b[0]);
}

export function extractUIDs(node) {
  let uids = [];

  // Check if there are children and recurse through them
  if (node.children && node.children.length > 0) {
    node.children.forEach(child => {
      uids = uids.concat(extractUIDs(child)); // Recursively call extractUIDs for each child
    });
  }

  // If there are no children but there are entries, extract the uids from entries
  if (node.entries && node.entries.length > 0) {
    node.entries.forEach(entry => {
      if (entry.itemId) {
        uids.push(entry.itemId); // Collect the itemId from the entries
      }
    });
  }

  return uids;
}

export function getUids(data) {
  const uids = [];

  // Iterate through components
  data.components.component.forEach(component => {
    // Check if the component has entries or if its children have entries
    if (component.entries && component.entries.length > 0) {
      component.entries.forEach(entry => {
        if (entry.itemId) uids.push(entry.itemId); // push itemId of entries
      });
    }

    // Check if the component has children
    if (component.children && component.children.length > 0) {
      component.children.forEach(child => {
        if (child.uid) uids.push(child.uid); // push uid of children
      });
    }

    // If the component itself has a uid, add it to the uids array
    if (component.uid) uids.push(component.uid);
  });

  return { position: data?.position, uids: uids };
}

// Modify getFooterComponentData to handle errors and ensure consistent return format
export async function getFooterComponentData(footerComponent) {
  if (!footerComponent) {
    // If there's no footerComponent, return an empty object or array to ensure consistency
    return { component: [] };
  }

  try {
    // Extract the UIDs from the footerComponent and fetch the data
    const response = await getCMSComponentsFromHybrisAsync(extractUIDs(footerComponent));

    // Assuming response has a `component` property, return it
    return response?.component ? { component: response.component } : { component: [] };
  } catch (error) {
    // Log the error and return an empty array for component in case of failure
    console.error('Error fetching footer component data:', error);

    // Return a fallback value with an empty array for components
    return { component: [] };
  }
}

/**
 * Converts a Unix timestamp to a locale date string.
 *
 * @param {number} timestamp Unix timestamp.
 * @returns {string} A locale date string in the format "Wed 16 Jun 2021".
 */
export function convertTimeStampToDate(timestamp: number): string {
  return new Date(timestamp)
    .toLocaleDateString('en-GB', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
    .replace(/,/g, '');
}

// Function to extract `uid` values from the innermost level and group them by parent `title`
export async function extractUidsWithTitle(node) {
  const result = [];

  // Helper function to process each node recursively
  async function processNode(node) {
    // Check if the current node has children
    if (node.children && node.children.length > 0) {
      for (const child of node.children) {
        // Check if the child has further children (children.children)
        if (child.children && child.children.length > 0) {
          // Extract itemIds from the children of this child node
          const itemIds = child.children
            .filter(grandchild => grandchild.entries && grandchild.entries.length > 0) // Ensure entries exist
            .map(grandchild => grandchild.entries.map(entry => entry.itemId)) // Extract itemIds
            .flat(); // Flatten the array of itemIds

          // Add the child title and the list of itemIds to the result array
          result.push({
            title: child.title || child.name, // Using title or name (whichever is available)
            components: itemIds,
          });
        }

        // Recursively process any further children
        await processNode(child);
      }
    }
  }

  // Start processing from the root node
  await processNode(node);

  return result;
}

export function getAbsoluteUrl(url) {
  if (url) {
    if (url.startsWith('http')) {
      return url;
    } else {
      return `${encodedContextPath}${url}`;
    }
  }
  return '';
}

/**
 * Retrieves Tasheel information for a given product tile.
 *
 * @param {Object} tile - The product tile containing Tasheel price data.
 * @returns {Object} An object containing formatted Tasheel price, currency,
 *          installment months, and percentage if available, otherwise an empty object.
 */
export function getTasheelInfo(tile) {
  // Retrieve Tasheel configuration from the app configuration
  const tasheelInfo = getAppConfig()?.unbxdSearchConfig?.tasheelInfo;
  let tasheelData = {};

  // Check if Tasheel information is available and populate tasheelData
  if (Object.keys(tasheelInfo)) {
    tasheelData = {
      value:
        tile?.tasheelPriceData?.priceValueTasheel?.formattedValue ??
        tile?.priceValueTasheel?.value ??
        tile?.priceValueTasheel,
      currency: getAppConfig()?.currency,
      tasheelInstallmentMonths: tasheelInfo.tasheelInstallmentMonths,
      tasheelPercentage: tasheelInfo.tasheelPercentage,
    };
  }

  // Return the populated Tasheel data or an empty object
  return tasheelData;
}

/**
 * Returns a formatted label indicating the number of offers.
 *
 * @param {number} count - The number of offers.
 * @returns {string | number} A formatted string with the count and appropriate label,
 *                            or 0 if the count is invalid or not positive.
 */
export function getOfferLabel(count) {
  const offersLabel = getStr('extra.product.cashback.offers');
  const offerLabel = getStr('extra.pdp.offerlable');

  // Ensure `count` is a number, defaulting to 0 if it's invalid
  if (typeof count !== 'number' || isNaN(count)) {
    console.warn('Invalid count value:', count); // Optional: Log warning for invalid values
    return 0;
  }

  // Return based on the value of count
  if (count === 1) {
    return `${count} ${offerLabel}`; // Use singular label for single offer
  } else if (count > 1) {
    return `${count} ${offersLabel}`; // Use plural label for multiple offers
  }

  return 0; // Default return value if count is not positive
}

export function getFreeGiftLength(data) {
  // Validate the input is an object and handle missing or unexpected fields
  if (typeof data !== 'object' || data === null) {
    console.warn('Invalid data input:', data);
    return 0;
  }

  // Case 1: 'freegiftworth' exists in data and has a valid format
  if ('freegiftworth' in data && typeof data.freegiftworth === 'string') {
    const freegiftArray = data.freegiftworth.split('-');
    const sourceQuantity = Number(freegiftArray[2]) || 0; // Safely convert to number or fallback to 0
    return sourceQuantity;
  }

  // Case 2: 'freeGiftQtuantities' exists and has a 'giftProductCount'
  if (Array.isArray(data.freeGiftQtuantities)) {
    const giftProductCountEntry = data.freeGiftQtuantities.find(x => x.key === 'giftProductCount');
    if (giftProductCountEntry) {
      return Number(giftProductCountEntry.value) || 0; // Safely convert value to number or fallback to 0
    }
  }

  // Default return value for any other cases
  return 0;
}

/**
 * Proxy type allowing dynamic query param access.
 */
type QueryParams = {
  [key: string]: string | null;
};

/**
 * Creates a proxy to access URLSearchParams like normal properties.
 * @param url Optional URL string. Defaults to window.location.search.
 */
export function createQueryParams(url?: string): QueryParams {
  const searchParams = url ? new URL(url).searchParams : new URLSearchParams(window.location.search);

  return new Proxy({} as QueryParams, {
    get: (_, prop: string) => searchParams.get(prop),
  });
}

/**
 * Returns the uiExperienceLevel in uppercase, or null.
 */
export function getUIExperienceLevel(url?: string): string | null {
  const params = createQueryParams(url);
  return params.uiExperienceLevel?.toUpperCase() ?? null;
}

/**
 * Checks whether uiExperienceLevel is 'MOBILE'.
 */
export function isMobileUIExperience(url?: string): boolean {
  return getUIExperienceLevel(url) === 'MOBILE';
}

/**
 * This function checks if the root page (like /en-sa) is served
 * from cloudflare cache.
 * @returns {boolean} true if served from cache, false otherwise.
 */
export function checkIfPageIsCached(): boolean {
  const loggedInCookieValue = getCookie('loggedIn');
  if (typeof loggedInCookieValue === 'string' && loggedInCookieValue.toLowerCase() === 'y') {
    return false;
  }
  return true;
}

/**
 * This function updates city in the header. It is assumed that the page is cached.
 * <b>Not meant to be called directly.</b>
 * @returns a promise which resolves to true, if the city was updated by this method.
 */
export function updateCityInHeaderAsync(): Promise<boolean> {
  return getCartLoginDetails().then(cld => {
    const headerCityEl = document.querySelector('.c_nav-location-current.js-user-select-city span');
    if (headerCityEl) {
      headerCityEl.textContent = cld?.city[getLangSpecificFieldname('name')];
      if (typeof window?.ACC?.config === 'object') {
        const cityProperty = cld.citySelected ? 'userSelectedCityCode' : 'userDefaultCityCode';
        window.ACC.config[cityProperty] = cld.city.code;
      }
      return true;
    }
    return false;
  });
}

/**
 * This function updates mini cart count in the header. It is assumed that the page is cached.
 * <b>Not meant to be called directly.</b>
 * @returns a promise which resolves to true, if the mini cart count was updated by this method.
 */
export function updateMiniCartCountInHeaderAsync(): Promise<boolean> {
  return getCartLoginDetails().then(cld => {
    const headerMiniCartEl = document.querySelector('.js-mini-cart-total');
    if (headerMiniCartEl) {
      headerMiniCartEl.textContent = cld?.cartData.totalUnitCount;
      return true;
    }
    return false;
  });
}

/**
 * This function updates header details like city and mini cart count.
 * It is called onMount of homepage. It only updates header if the root
 * document was served from cloudflare cache.
 * @returns true if the header was actually updated from this method.
 */
export function updateHeaderForCachedPageAsync(): Promise<boolean> {
  if (checkIfPageIsCached()) {
    return Promise.all([updateCityInHeaderAsync(), updateMiniCartCountInHeaderAsync()]).then(bools =>
      bools.some(b => b)
    );
  }

  return Promise.resolve(false);
}

export type IOOptions = { once: boolean; callback: (node: HTMLElement) => void };
/**
 * Observes an HTMLElement for intersection with the viewport and calls a callback.
 * @param node {HTMLElement} Target element to watch for intersection.
 * @param params {}
 */
export function intersectionObserver(node: HTMLElement, params: IOOptions) {
  const options = {
    root: null,
    rootMargin: '0px',
    threshold: 0.5,
  };

  const observer = new IntersectionObserver(handleIntersectFactory(node, params), options);
  observer.observe(node);
}

function handleIntersectFactory(node: HTMLElement, params: IOOptions): IntersectionObserverCallback {
  return (entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        console.log('Entry is intersecting');
        if (params?.callback) {
          params.callback(node);
        }
        if (params?.once) {
          observer.unobserve(node);
        }
      }
    });
  };
}

export function replaceNewlinesToBRs(inputText: string): string {
  return inputText?.replaceAll('\n', '<br>') || '';
}

export function checkIsTacticalPromoAvailable($ps) {
  const userType = getJoodExpiredMembershipType() || getJoodMembershipType();
  if (
    (userType === 'NOPRIME' && ($ps?.tacticalVipPromo || $ps?.productVipPrimeData?.tacticalPromo)) ||
    (userType === 'VIPPRIME' && ($ps?.tacticalVipPromo || $ps?.productVipPrimeData?.tacticalPromo)) ||
    (userType === 'BASICPRIME' && ($ps?.tacticalBasicPromo || $ps?.productBasicPrimeData?.tacticalPromo))
  ) {
    return userType;
  }
  return null;
}
export function getJoodDayTheme($ps) {
  const joodType = checkIsTacticalPromoAvailable($ps);
  if (joodType) {
    const borderColor = getStr(`extra.joodday.tile.border.${joodType}`);
    return borderColor ? { borderColor, joodType, joodDayRemainingQuantity: $ps?.joodDayRemainingQuantity } : {};
  }
  return {};
}

export function getProductFinalPricingData(productOrEntry) {
  const entry = productOrEntry?.product ? productOrEntry : { product: productOrEntry };
  const product = entry.product ?? productOrEntry;
  const {
    basePrice,
    cashBackPrice,
    discountPrice,
    price,
    isDigitalGiftCard,
    percentageDiscount,
    productBasicPrimeData,
    productVipPrimeData,
  } = product ?? {};
  const { basePrice: entryBasePrice } = entry ?? {};
  const userType = getJoodMembershipType();
  let finalPrice = price?.value;
  let finalDiscountPercentage = 0;
  let hasPrimePricing = false;

  if (userType === 'BASICPRIME' && productBasicPrimeData?.tacticalPrice?.value > 0) {
    finalPrice = productBasicPrimeData.tacticalPrice?.value;
    finalDiscountPercentage = productBasicPrimeData.tacticalPercentageDiscount?.value || 0;
    hasPrimePricing = true;
  } else if (userType === 'VIPPRIME' && productVipPrimeData?.tacticalPrice?.value > 0) {
    finalPrice = productVipPrimeData.tacticalPrice?.value;
    finalDiscountPercentage = productVipPrimeData.tacticalPercentageDiscount?.value || 0;
    hasPrimePricing = true;
  } else if (userType === 'BASICPRIME' && productBasicPrimeData?.primePrice?.value > 0) {
    finalPrice = productBasicPrimeData.primePrice?.value;
    finalDiscountPercentage = productBasicPrimeData.primePercentageDiscount?.value || 0;
    hasPrimePricing = true;
  } else if (userType === 'VIPPRIME' && productVipPrimeData?.primePrice?.value > 0) {
    finalPrice = productVipPrimeData.primePrice?.value;
    finalDiscountPercentage = productVipPrimeData.primePercentageDiscount?.value || 0;
    hasPrimePricing = true;
  } else if (isDigitalGiftCard) {
    finalPrice = basePrice?.value ?? entryBasePrice?.value;
  } else if (cashBackPrice && discountPrice) {
    finalPrice = roundDownHalfToFixedPlaces(discountPrice.value - cashBackPrice, currencyDecimal);
  } else if (discountPrice) {
    finalPrice = discountPrice.value;
  } else if (cashBackPrice) {
    finalPrice = roundDownHalfToFixedPlaces(price.value - cashBackPrice, currencyDecimal);
  } else {
    finalPrice = price?.value;
  }

  if (!hasPrimePricing && !isDigitalGiftCard && percentageDiscount) {
    finalDiscountPercentage = percentageDiscount.value;
  }

  return {
    price: finalPrice,
    discountPercentage: finalDiscountPercentage,
  };
}
export function getConditionBuyBoxes(product: any): any[] {
  if (!product?.conditionBuyBoxes) {
    return [];
  }

  if (Array.isArray(product.conditionBuyBoxes)) {
    return product.conditionBuyBoxes;
  }

  try {
    return JSON.parse(product.conditionBuyBoxes);
  } catch (error) {
    console.warn('Unable to parse conditionBuyBoxes', error);
    return [];
  }
}

export function resolveDisplayOffer(product: any, selectedConditions: string[] = []) {
  const buyBoxes = getConditionBuyBoxes(product);

  if (!selectedConditions?.length) {
    return { ...product };
  }

  for (const priority of APP_CONSTANTS.PRODUCT_CONDITION_PRIORITY) {
    if (!selectedConditions.includes(priority)) {
      continue;
    }

    if (priority === product.condition) {
      return { ...product };
    }

    const matched = buyBoxes.find(box => box.condition === priority);

    if (matched) {
      return {
        ...product,

        condition: matched.condition,
        offerId: matched.offerId,
        price: matched.price,
        wasPrice: matched.wasPrice,
        discount: matched.discount,
        priceValueDiscountPercentage: matched.priceValueDiscountPercentage,
        priceValueDiscountPercentageFlag: matched.priceValueDiscountPercentageFlag,
        priceMonthsTasheel: matched.priceMonthsTasheel ?? product.priceMonthsTasheel,
        priceValueTasheel: matched.priceValueTasheel ?? product.priceValueTasheel,
      };
    }
  }

  return { ...product };
}
